import React from "react";
import { Link } from "react-router-dom";
import { formatWholeCurrency, formatUOM } from "../aabiFormatters";
import { MEASURE_LABELS } from "../aabiConstants";
import "./../AABI.css";

const formatValue = (value, mode) =>
  mode === "uom" ? formatUOM(value ?? 0) : formatWholeCurrency(value ?? 0);

const formatChange = (current, prior, mode) => {
  if (prior === 0) return "New";
  const ratio = (current - prior) / prior;
  const sign = ratio > 0 ? "+" : ratio < 0 ? "-" : "";
  if (mode === "uom") return `${sign}${Math.round(Math.abs(ratio * 100))}%`;
  return `${sign}${Math.abs(ratio * 100).toFixed(1)}%`;
};

// Semantic table equivalent of the trend chart (§6.1): every bucket's actual
// dates and both series' values — the accessible, export-ready representation.
//
// Additive props for the Executive Dashboard (defaults keep today's table):
//   measure     "current" | "realized"; when set the table follows the selected
//               measure and its header names it (otherwise: Current revenue)
//   bucketLink  (bucket) => url; the bucket label becomes a link, which is the
//               keyboard path for the chart's click-through
const metricOf = (bucket, mode, measure) => {
  if (!bucket) return undefined;
  if (mode === "uom") return bucket.uom;
  return measure === "realized" ? bucket.realizedRevenue : bucket.currentRevenue;
};

const TrendDataTable = ({ trend, mode, measure, bucketLink }) => {
  const current = trend?.currentSeries ?? [];
  const prior = trend?.priorSeries ?? [];

  const valueHeader =
    mode === "uom" ? "UOM" : measure ? MEASURE_LABELS[measure] : "Revenue";

  return (
    <div className="aabi-table-frame">
      <table className="aabi-data-table">
        <caption className="aabi-visually-hidden">
          {valueHeader} by time bucket for the selected and preceding periods
        </caption>
        <thead>
          <tr>
            <th scope="col">Bucket</th>
            <th scope="col">Current dates</th>
            <th scope="col" className="aabi-numeric">
              {valueHeader}
            </th>
            <th scope="col">Prior dates</th>
            <th scope="col" className="aabi-numeric">
              Prior {valueHeader.toLowerCase()}
            </th>
            <th scope="col" className="aabi-numeric">
              Change
            </th>
          </tr>
        </thead>
        <tbody>
          {current.map((bucket, index) => {
            const priorBucket = prior[index];
            const value = metricOf(bucket, mode, measure);
            const priorValue = metricOf(priorBucket, mode, measure);
            return (
              <tr key={bucket.index ?? index}>
                <th scope="row">
                  {bucketLink ? (
                    <Link className="aabi-table-link" to={bucketLink(bucket)}>
                      {bucket.label}
                    </Link>
                  ) : (
                    bucket.label
                  )}
                </th>
                <td>
                  {bucket.startDate} – {bucket.endDate}
                </td>
                <td className="aabi-numeric">{formatValue(value, mode)}</td>
                <td>
                  {priorBucket
                    ? `${priorBucket.startDate} – ${priorBucket.endDate}`
                    : "-"}
                </td>
                <td className="aabi-numeric">
                  {priorBucket ? formatValue(priorValue, mode) : "-"}
                </td>
                <td className="aabi-numeric">
                  {priorBucket ? formatChange(value ?? 0, priorValue ?? 0, mode) : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TrendDataTable;
